// src/components/BulkCompanyPermissionsDialog.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  Autocomplete,
  TextField,
  Chip,
  Avatar,
  Stack,
  CircularProgress,
} from "@mui/material";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import { fetchData } from "../api/client";
import { useTranslation } from "react-i18next";
import {
  extractCompanyAclUsers,
  mergeAutocompleteUsers,
  normalizeAclUser,
} from "../utils/companyAcl";

function setsOfIdsEqual(sets) {
  if (sets.length <= 1) return true;
  const base = [...sets[0]].sort((a, b) => a - b).join(",");
  return sets.every((s) => [...s].sort((a, b) => a - b).join(",") === base);
}

export default function BulkCompanyPermissionsDialog({
  open,
  onClose,
  companyIds = [],
  companiesById = {},
  onSuccess,
}) {
  const { t } = useTranslation("settings");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [usersQuery, setUsersQuery] = useState("");
  const [usersLoading, setUsersLoading] = useState(false);
  const [aclLoading, setAclLoading] = useState(false);
  const [mixedWarning, setMixedWarning] = useState(false);
  const [aclPartialLoad, setAclPartialLoad] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const companyIdsKey = useMemo(
    () =>
      [...companyIds]
        .map((id) => Number(id))
        .filter(Boolean)
        .sort((a, b) => a - b)
        .join(","),
    [companyIds]
  );

  useEffect(() => {
    if (!open || !companyIdsKey) return;
    let alive = true;
    setError(null);
    setMixedWarning(false);
    setAclPartialLoad(false);
    setUsersQuery("");
    setSelectedUsers([]);
    setAclLoading(true);

    const ids = companyIdsKey.split(",").map(Number).filter(Boolean);

    Promise.allSettled(ids.map((cid) => fetchData(`/companies/${cid}/acl`, "GET")))
      .then((settled) => {
        if (!alive) return;
        const perCompany = [];
        let failed = 0;
        settled.forEach((r, idx) => {
          if (r.status === "fulfilled") {
            perCompany.push({
              companyId: ids[idx],
              users: extractCompanyAclUsers(r.value),
            });
          } else {
            failed += 1;
          }
        });

        if (perCompany.length === 0) {
          setError(t("companyTable.bulkPermissionsLoadAclError"));
          setSelectedUsers([]);
          return;
        }

        if (failed > 0) setAclPartialLoad(true);

        if (perCompany.length === 1) {
          setSelectedUsers(perCompany[0].users);
          setMixedWarning(false);
          return;
        }

        const idSets = perCompany.map((p) => new Set(p.users.map((u) => Number(u.id))));
        const same = setsOfIdsEqual(idSets);

        const byId = new Map();
        perCompany.forEach((p) => {
          p.users.forEach((u) => {
            const n = normalizeAclUser(u);
            if (n) byId.set(n.id, n);
          });
        });
        setSelectedUsers([...byId.values()]);
        setMixedWarning(!same);
      })
      .catch(() => {
        if (!alive) return;
        setError(t("companyTable.bulkPermissionsLoadAclError"));
        setSelectedUsers([]);
      })
      .finally(() => alive && setAclLoading(false));

    return () => {
      alive = false;
    };
  }, [open, companyIdsKey, t]);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    const term = String(usersQuery || "").trim();
    const timer = setTimeout(() => {
      setUsersLoading(true);
      fetchData(`/users?search=${encodeURIComponent(term)}&limit=20`, "GET")
        .then((resp) => {
          if (!alive) return;
          const rows = Array.isArray(resp?.data) ? resp.data : [];
          setUserOptions(rows.map((u) => ({ id: u.id, nome: u.nome || "", email: u.email || "" })));
        })
        .catch(() => alive && setUserOptions([]))
        .finally(() => alive && setUsersLoading(false));
    }, 300);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [usersQuery, open]);

  const mergedOptions = useMemo(
    () => mergeAutocompleteUsers(userOptions, selectedUsers),
    [userOptions, selectedUsers]
  );

  const handleApply = async () => {
    const users = selectedUsers.map((u) => Number(u.id)).filter(Boolean);
    if (!users.length) {
      setError(t("companyTable.bulkPermissionsNeedUsers"));
      return;
    }
    const ids = companyIdsKey.split(",").map(Number).filter(Boolean);
    if (!ids.length) {
      setError(t("companyTable.bulkPermissionsNoCompanies"));
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const results = await Promise.allSettled(
        ids.map((companyId) => fetchData(`/companies/${companyId}/acl`, "PUT", { users }))
      );
      const failed = results.filter((r) => r.status === "rejected");
      if (failed.length === results.length) {
        setError(t("companyTable.bulkPermissionsAllFailed"));
        return;
      }
      onSuccess?.({ failed: failed.length, total: results.length });
      onClose?.();
    } catch (e) {
      setError(
        (typeof e?.message === "string" && e.message) || t("companyTable.bulkPermissionsError")
      );
    } finally {
      setSaving(false);
    }
  };

  const companyLabels = companyIds
    .map((id) => {
      const n = companiesById[Number(id)];
      return n ? `${n} (#${id})` : `#${id}`;
    })
    .join(", ");

  return (
    <Dialog open={open} onClose={saving || aclLoading ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <SecurityRoundedIcon color="primary" />
        {t("companyTable.bulkPermissionsTitle")}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("companyTable.bulkPermissionsIntro", { count: companyIds.length })}
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 2 }}>
          {companyLabels}
        </Typography>

        {aclLoading && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              {t("companyTable.bulkPermissionsLoadingAcl")}
            </Typography>
          </Stack>
        )}

        {aclPartialLoad && !aclLoading && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t("companyTable.bulkPermissionsPartialLoad")}
          </Alert>
        )}

        {mixedWarning && !aclLoading && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t("companyTable.bulkPermissionsMixedWarning")}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Autocomplete
          multiple
          disableCloseOnSelect
          filterSelectedOptions
          options={mergedOptions}
          value={selectedUsers}
          loading={usersLoading}
          disabled={aclLoading}
          onChange={(_, val) => setSelectedUsers(val)}
          onInputChange={(_, val) => setUsersQuery(val)}
          getOptionLabel={(opt) =>
            opt?.nome ? `${opt.nome}${opt.email ? ` — ${opt.email}` : ""}` : opt?.email || ""
          }
          isOptionEqualToValue={(o, v) => Number(o.id) === Number(v.id)}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Avatar sx={{ width: 24, height: 24 }}>
                  {(option.nome || option.email || "?").slice(0, 1).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography fontSize={14} fontWeight={600}>
                    {option.nome || "—"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.email || ""}
                  </Typography>
                </Box>
              </Stack>
            </li>
          )}
          renderTags={(value, getTagProps) =>
            value.map((opt, idx) => (
              <Chip
                {...getTagProps({ index: idx })}
                key={opt.id}
                label={opt.nome || opt.email}
                variant="outlined"
                size="small"
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("addCompanyForm.permissions.usersLabel")}
              placeholder={t("addCompanyForm.permissions.searchPlaceholder")}
              helperText={
                mixedWarning
                  ? t("companyTable.bulkPermissionsHelperMixed")
                  : t("companyTable.bulkPermissionsHelper")
              }
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {usersLoading ? <CircularProgress size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              fullWidth
            />
          )}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving || aclLoading}>
          {t("shared.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={saving || aclLoading}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {saving ? t("shared.loading") : t("companyTable.bulkPermissionsApply")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
